import {
  RigNames,
  TimeGranularityOptionValues,
} from 'pages/EnergyEfficiency/types/energyEfficiency.types';

import { WellStatusValues } from './well';

export enum INDICATOR_GRANULARITY_VALUES {
  HOUR = 'hour',
  DAY = 'day',
  MONTH = 'month',
}

export enum INDICATOR_NAME_VALUES {
  AVG_LOAD_GENERATOR_1 = 'average_load_generator_1',
  AVG_LOAD_GENERATOR_2 = 'average_load_generator_2',
  AVG_LOAD_GENERATOR_3 = 'average_load_generator_3',
  AVG_LOAD_GENERATOR_4 = 'average_load_generator_4',
  AVG_LOAD_GENERATOR_5 = 'average_load_generator_5',
  AVG_LOAD_GENERATOR_6 = 'average_load_generator_6',
  FUEL_CONSUMPTION_GENERATOR_1 = 'fuel_consumption_generator_1',
  FUEL_CONSUMPTION_GENERATOR_2 = 'fuel_consumption_generator_2',
  FUEL_CONSUMPTION_GENERATOR_3 = 'fuel_consumption_generator_3',
  FUEL_CONSUMPTION_GENERATOR_4 = 'fuel_consumption_generator_4',
  FUEL_CONSUMPTION_GENERATOR_5 = 'fuel_consumption_generator_5',
  FUEL_CONSUMPTION_GENERATOR_6 = 'fuel_consumption_generator_6',
  CURRENT_DIRECTION = 'current_direction',
  CURRENT_SPEED = 'current_speed',
  DRL_PACKAGE_POWER = 'drl_package_power',
  DRL_PACKAGE_TREE_STRUCTURE = 'drl_package_tree_structure',
  GLOBAL_AVG_GENERATOR_LOAD = 'global_average_generator_load',
  GRANULAR_FUEL_CONSUMPTION = 'global_fuel_consumption',
  NB_WORKING_GENERATORS = 'number_of_working_generators',
  POWER_CONSUMPTION_PACKAGES = 'power_consumption_packages',
  STK_PACKAGE_POWER = 'stk_package_power',
  STK_PACKAGE_TREE_STRUCTURE = 'stk_package_tree_structure',
  UT_PACKAGE_POWER = 'ut_package_power',
  UT_PACKAGE_TREE_STRUCTURE = 'ut_package_tree_structure',
  VESSEL_HEADING = 'vessel_heading',
  WAVE_DIRECTION = 'wave_direction',
  WAVE_HEIGHT = 'wave_height',
  WIND_DIRECTION = 'wind_direction',
  WIND_SPEED = 'wind_speed',
}

export type IndicatorDataPoint = {
  value: number;
  date: string;
};

type BaseRigIndicatorDTO = {
  rigId: string;
  name: INDICATOR_NAME_VALUES;
  startDate: string;
  endDate: string;
  granularity: TimeGranularityOptionValues;
  unit: string | null;
};

export type RigIndicatorDTO = BaseRigIndicatorDTO & {
  data: IndicatorDataPoint[];
};

export type BaseIndicatorBySelectedDateDTO = {
  rigId: string;
  selectedDate: string;
  granularity: TimeGranularityOptionValues;
};

export type IndicatorsBySelectedDateDTO<DataPoint> = BaseIndicatorBySelectedDateDTO & {
  data: DataPoint[];
};

export type PackageIndicatorFormattedValue = {
  value: number | null;
  unit: string | null;
  treeStructureIndicator: INDICATOR_NAME_VALUES;
};
export type PowerConsumptionPackageItem = {
  name: string;
  color: string;
} & PackageIndicatorFormattedValue;

export type ConsumptionPowerPackageDTO = BaseRigIndicatorDTO & {
  drlPackagePower: PackageIndicatorFormattedValue;
  stkPackagePower: PackageIndicatorFormattedValue;
  utPackagePower: PackageIndicatorFormattedValue;
};

export type GeneratorsAverageLoadDetailsDataPoint = {
  name:
    | INDICATOR_NAME_VALUES.AVG_LOAD_GENERATOR_1
    | INDICATOR_NAME_VALUES.FUEL_CONSUMPTION_GENERATOR_1
    | INDICATOR_NAME_VALUES.AVG_LOAD_GENERATOR_2
    | INDICATOR_NAME_VALUES.FUEL_CONSUMPTION_GENERATOR_2
    | INDICATOR_NAME_VALUES.AVG_LOAD_GENERATOR_3
    | INDICATOR_NAME_VALUES.FUEL_CONSUMPTION_GENERATOR_3
    | INDICATOR_NAME_VALUES.AVG_LOAD_GENERATOR_4
    | INDICATOR_NAME_VALUES.FUEL_CONSUMPTION_GENERATOR_4
    | INDICATOR_NAME_VALUES.AVG_LOAD_GENERATOR_5
    | INDICATOR_NAME_VALUES.FUEL_CONSUMPTION_GENERATOR_5
    | INDICATOR_NAME_VALUES.AVG_LOAD_GENERATOR_6
    | INDICATOR_NAME_VALUES.FUEL_CONSUMPTION_GENERATOR_6;
  value: number;
};

export type FuelConsumptionGeneratorsData = {
  data: GeneratorsAverageLoadDetailsDataPoint[];
};

export type GeneratorsAverageLoadDetailsDTO =
  IndicatorsBySelectedDateDTO<GeneratorsAverageLoadDetailsDataPoint>;

export type MainOperatingPhilosophyDTO = BaseIndicatorBySelectedDateDTO & { isClosedBus: boolean };

export type WeatherDataPoint = {
  name:
    | INDICATOR_NAME_VALUES.CURRENT_DIRECTION
    | INDICATOR_NAME_VALUES.CURRENT_SPEED
    | INDICATOR_NAME_VALUES.VESSEL_HEADING
    | INDICATOR_NAME_VALUES.WAVE_DIRECTION
    | INDICATOR_NAME_VALUES.WAVE_HEIGHT
    | INDICATOR_NAME_VALUES.WIND_DIRECTION
    | INDICATOR_NAME_VALUES.WIND_SPEED;
  value: number;
  unit: string | null;
};

export type WeatherDTO = IndicatorsBySelectedDateDTO<WeatherDataPoint>;

export type PowerConsumptionPackageData = {
  drillingConsumption: number;
  stationKeepingConsumption: number;
  utilitiesConsumption: number;
};

export interface LegacyOperationOverviewData {
  cdfData: false;
  wellId: string | null;
  majorOperationCodes: string[];
  drillingPhase: string | null;
}
export interface OperationOverviewData {
  cdfData: true;
  wellName: string | null;
  majorOperationCodes: string[];
  drillingPhase: string | null;
}

export type WeatherData = Record<WeatherDataPoint['name'], WeatherDataPoint>;

export type LegacyOperationOverviewDTO = BaseIndicatorBySelectedDateDTO &
  LegacyOperationOverviewData;
export type OperationOverviewDTO = BaseIndicatorBySelectedDateDTO & OperationOverviewData;

export type PowerConsumptionPackageDetailsData = {
  name: string;
  value: number | null;
  unit: string | null;
  isNode: boolean;
};

export type PowerConsumptionPackageDetailsDTO = BaseRigIndicatorDTO & {
  data: PowerConsumptionPackageDetailsData[];
};

export type RigsByWellDTO = { wellId: string; rigIds: string[] };

export type EnergyEfficiencyDTO = VesselWellContextDTO & WellDetailsDTO & RigConnectionStatusDTO;

export type VesselWellContextDTO = {
  description: string;
  rigName: RigNames;
  endTimestamp: number;
  startTimestamp: number;
  wellExternalId: string;
  wellId: string;
};
export type DrillingPowerConsumptionPerPhaseDataPoint = {
  code: string;
  power: number;
  durationInHours: number;
};

export type DrillingPowerConsumptionPerPhaseDTO = {
  wellId: string;
  section: string;
  data: DrillingPowerConsumptionPerPhaseDataPoint[];
  powerUnit: string | null;
};

export type RigSpecificationsDetails = {
  rigType: string;
  yearBuilt: number;
  dimensions: string;
  flag: string;
  imo: number;
};

export type RigSpecificationsDTO = {
  rigId: string;
  rigName: string;
  rigFuelConsumptionCalculationInfo: string;
  specifications: RigSpecificationsDetails;
};

export type RigDurationDTO = {
  id: string;
  wellName: string;
  rigName: string;
  startDate: string;
  endDate: string;
};

export type WellCumulativeFuelConsumptionDTO = {
  wellId: string;
  section: string;
  value: number;
  unit: string | null;
};

export type WellIndicatorDTO = {
  wellId: string;
  name: string;
  granularity: INDICATOR_GRANULARITY_VALUES;
  data: IndicatorDataPoint[];
  unit?: string | null;
};

export type WellPhaseCoefficient = {
  phaseName: string;
  coefficient: number;
};

export type WellPhasesCoefficientsDTO = {
  wellId: string;
  coefficients: WellPhaseCoefficient[];
  unit?: string;
};

export type WellDetailsDTO = {
  wellId: string;
  wellName: string;
  wellStatus: WellStatusValues;
  waterDepth: number;
  country: string;
};

export enum RigConnectStatus {
  INPROGRESS = 'In Progress',
  NOTSTARTED = 'Not Started',
  DONE = 'Completed',
  BLOCKED = 'Blocked',
}

export type RigConnectionStatusDTO = {
  rigConnectStatus: RigConnectStatus;
  rigConnectStartDate: string | null;
  backfillingStatus: RigConnectStatus;
  backfillingStartDate: string | null;
  webConfigurationStatus: RigConnectStatus;
  webConfigurationStartDate: string | null;
  dataInjestionStatus: RigConnectStatus;
  dataInjestionStartDate: string | null;
  indicatorConfigurationStatus: RigConnectStatus;
  indicatorConfigurationStartDate: string | null;
  rigId: string;
  comment: string;
  rigIntegrationStatus: RigConnectStatus;
};
